import { ApiState } from "../../../../common/constants/api-constants";
import { CreateCustomProductMetadata } from '../../../create-custom/interfaces/create-custom-product.interface';
import { FreeTrialInfo } from "../../../free-trial/interfaces/free-trial-info.interface";
import { InitializeCartMagentoResponse, ValidateLoggedInUserConnectLicenseResponse } from "../../initialize-cart/interface/initialize-cart.interface";

export declare interface AvailabilityInfo {
  available: boolean;
  errorCode: string;
  errorMessage: string;
}

export declare interface PriceInfo {
  netPrice: number;
  listPrice: number;
  discount: number;
  currencyCode: string;
  countryCode: string;
  offerId: number;
  offerScope: string;
  offerType: string;
  offerPolicyName: string;
  entOfferId: string;
  customPrice: number;
  customPriceIdentifier: string;
  duration: string;
  durationPeriod: string;
  ebookId: string;
  isTunnelPurchase?: boolean;
}

export declare interface ProductDetails {
  title: string;
  contributors: string[];
  edition: number;
  editionOrRelease?: string;
  publicationDate: number;
  copyrightYear: number;
  format: string;
  formatCode: string;
  productType: string;
  imageUrl: string;
  restricted: boolean;
  llrestricted: boolean;
  digitalBundle: boolean;
  productBranding: string;
  productBrandingDesc: string;
  virtual: boolean;
  comingSoonProduct: boolean;
  grades: string;
  cardType: string;
  itemType: string;
  flow?: string;
  application: string;
  duration?: number;
  durationPeriod?: string;
  isEvergreen: boolean;
  offerType?: string;
  offerBranding?: string;
  isSharpenCompanion?: boolean;
  formatTypeDesc?: string;
  durationType?: string;
  metaOfferContentsMetadata?: MetaOfferContentsMetadata[];
  titleType?: string | null;
  division?: string | null;
}
type SegmentType = "HE" | "SEG" | "K12" | "PROFESSIONAL" | string;

export declare interface ProductIdentification {
  sku?: string;
  offerPricingId?: string;
  mhid: string | undefined;
  isbn10: string | undefined;
  isbn13: string | undefined;
  programSourceId?: string;
  programSourceName?: string;
  businessUnit: SegmentType;
  context: string | undefined;
  catalog: string | undefined;
  productType: string | undefined;
  looseLeafAvailable?: boolean;
  hasEbook?: boolean;
  isConnectSharpenBundle?: boolean;
  isSharpenCompanion?: boolean;
  isSharpenAdvantage?: boolean;
  term?: string;
}

export declare interface AddProductRequest {
  productIdentifier: ProductIdentification;
  quantity: number;
}

export declare interface MagentoProductIdentification {
  sku: string | undefined;
  qty: number | undefined;
  quote_id?: string | undefined;
  item_id?: string;
  extension_attributes?: {
    custom_store_code?: string;
    free_trial?: boolean;
    instructor_sampling?: boolean;
    product_offer_id?: string;
  };
}

export declare interface AddProductMagentoRequest {
  cartItem: MagentoProductIdentification;
}

export declare interface AddProductMagentoResponse {
  item_id: number;
  sku: string;
  qty: number;
  quote_id: string;
  product_type: string;
  extension_attributes?: {
    custom_store_code?: string;
    free_trial?: boolean;
    free_trial_replaced?: boolean;
    instructor_sampling?: boolean;
    product_offer_id?: string;
  };
}

export declare interface Product {
  item_id?: string;
  productIdentifier: ProductIdentification;
  productMetadata: ProductDetails;
  createCustomProductMetadata?: CreateCustomProductMetadata;
  quantity: number;
  priceInfo: PriceInfo;
  availabilityInfo?: AvailabilityInfo;
  freeTrialInfo?: FreeTrialInfo;
  modifiers: CustomerModifiers[];
  createdDate: number;
  modifiedDate: number;
  heebook: boolean;
  exitUrl?: string;
  cancelUrl?: string;
  quoteId?: string;
}

export declare interface CustomerModifiers {
  id: number;
  name: string;
  type: string;
}

export declare interface ShopCart {
  products: Product[];
  isValidInstructor?: boolean;
  sampleCart?: boolean;
  productCount: number;
  showPoOption: boolean;
  isPurchaseOrder?: boolean;
  poNumber?: string;
  cartId: string;
  discount: number;
  shippable: boolean;
  totalAmount: number;
  subTotal: number;
  bundleDiscount: number;
  metaOfferId?: string;
  orderBusinessUnit: string;
  userBusinessUnit: string;
  userRole: string;
  erpAccountNumber: string;
  store?: string;
  storeType?: StoreType.B2B | StoreType.B2C;
  currencyCode?: string;
  intlTaxAmount?: number;
  isTunnelPurchase?: boolean;
  errorMessage: string;
  availability: number;
  itemsCount: number;
  storeName?: string;
  guestCheckoutEmail?: string;
  addToCartResponse?: AddProductMagentoResponse;
}

export enum StoreType {
  B2B = "SB",
  B2C = "SC"
}

export enum BulkAddToCartResponseStatus {
  success = 'success',
  failure = 'failure'
}

export declare interface BulkAddToCartResponse {
  productIdentifier: {
    isbn10: string;
    isbn13: string;
    businessUnit: string;
  };
  quantity: number;
  status: BulkAddToCartResponseStatus;
  errorResponse?: {
    message: string;
    code: string;
  };
}

export declare interface ShopCartState {
  getShopCart: GetShopCartApiResponse;
  getCustomCart: GetShopCartApiResponse;
  bulkAddToCart: BulkAddToCartApiResponse;
  mergeGuestCart: MergeGuestCartApiResponse;
  mergeSharedCart: MergeSharedCartApiResponse;
  getGuestCartTokenMagento: GetGuestCartTokenMagentoApiResponse;
  createLoggedInCartQuoteIdMagento: CreateLoggedInCartQuoteIdMagentoApiResponse;
  initializeCartMagentoResponse: InitializeCartMagentoResponse;
  validateLoggedInUserConnectLicenseResponse: ValidateLoggedInUserConnectLicenseResponse;
  heInstructorInvalidProductsRemoved: boolean;
}

export declare interface CustomPriceRequest {
  billtoSiteId?: number;
  shiptoSiteId?: number;
  promoCode?: string;
}

export declare interface CustomPriceMagentoRequest {
  customPriceRequest: CustomPriceMagento;
}

export declare interface CustomPriceMagento {
  erpCustomerId?: string;
  billToSiteId?: number;
  shipToSiteId?: number;
}

export declare interface CustomPriceMagentoResponse {
  custom_price_available: boolean;
}

export declare interface GetShopCartApiResponse extends ApiState {
  data: ShopCart | undefined;
}
export declare interface MergeGuestCartApiResponse extends ApiState {
  data: ShopCart | undefined;
}
export declare interface MergeSharedCartApiResponse extends ApiState {
  data: ShopCart | undefined;
}

export declare interface BulkAddToCartApiResponse extends ApiState {
  data: BulkAddToCartResponse[] | undefined;
}

export declare interface GetGuestCartTokenMagentoApiResponse extends ApiState {
  data: string | undefined;
}

export declare interface CreateLoggedInCartQuoteIdMagentoApiResponse extends ApiState {
  data: string | undefined;
}

export declare interface GetShopCartParams {
  forceUpdate?: boolean;
  store?: string;
  promoCode?: string;
}

export declare interface AddToCartUrlContextParam {
  businessUnit: string;
  isbn: string;
  isbn13?: string;
  quantity: number;
  freeTrial?: boolean;
  offerPricingId?: string;
  instructorSampling?: boolean;
}

export declare interface BulkAddToCartThunkParam {
  isbns: string[];
  addItemsToMineCart?: boolean;
}

export declare interface MetaOfferContentsMetadata {
  productOfferId?: string;
  title?: string;
  offerType?: string;
  isbn10?: string;
  isbn13?: string;
  primary?: boolean;
  duration?: number;
}
